import React from 'react'
import * as PropTypes from 'prop-types'
import {Container, Row, Col, Caption} from '@modifi/ui'
import {InferPropTypes} from '@modifi/typescript-utils'
import {extractFromLocale, KEY_JANUS, Trans, useTranslation} from '@modifi/plugin-i18n'
import {white} from '@modifi/colors'

import {TRACK_ACTION_NAVIGATE} from '../../../constants/tracking'
import {cookieSettingsPath} from '../../../constants/routes'
import {FooterWrapper, LogoLink, Nav, NavItem, H4, Copyright} from './styles'
import Link from '../../../features/common/link'
import {removeUserConsentAndReload} from '../../../utils/consent-cookie'
import {FooterSection, CountryAwareBrandLogo, isCnTldOrRegion} from './components'
import {COMPANY} from '../../..'

const footerPropTypes = {
  isLandingPage: PropTypes.bool,
  footerRoutes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          path: PropTypes.string,
        }).isRequired
      ),
    }).isRequired
  ).isRequired,
  extraLinks: PropTypes.shape({
    facebook: PropTypes.string,
    home: PropTypes.string,
    linkedIn: PropTypes.string,
    login: PropTypes.string,
    signup: PropTypes.string,
    twitter: PropTypes.string,
  }).isRequired,
  footerText: PropTypes.string,
  footerSubText: PropTypes.string,
}

const footerDefaultPropTypes = {
  isLandingPage: false,
  footerText: undefined,
  footerSubText: undefined,
}

export type FooterProps = InferPropTypes<typeof footerPropTypes, typeof footerDefaultPropTypes>

const Footer: React.FC<FooterProps> = ({
  isLandingPage,
  footerRoutes,
  extraLinks,
  footerText,
  footerSubText,
}) => {
  const {t, i18n} = useTranslation(KEY_JANUS)

  const [, region] = extractFromLocale(i18n.language)

  if (footerRoutes.length === 0) {
    return null
  }
  return (
    <FooterWrapper>
      <Container>
        <Row>
          <Col lg={3}>
            <LogoLink
              to={extraLinks?.home || '/'}
              scrollToTop
              aria-label="Home"
              data-track-click-action={TRACK_ACTION_NAVIGATE}
              data-track-click-label="MODIFI Logo Footer"
            >
              <CountryAwareBrandLogo />
            </LogoLink>
            <FooterSection first t={t} extraLinks={extraLinks} />
          </Col>
          <Col lg={9}>
            <Row xlJustifyContent="around" smJustifyContent="between">
              {footerRoutes.map(({type: routeType, items, label: labelKey}) => {
                const label = t(labelKey)
                const noOfCategories = footerRoutes.length
                const setColumns = 12 % noOfCategories === 0 && 12 / noOfCategories > 2
                return (
                  <Col
                    key={`col-${routeType === 'sub-navigation' ? label : 'general-routes'}`}
                    col={6}
                    lg={setColumns ? 12 / noOfCategories : 'auto'}
                  >
                    <H4>{label !== 'general-route' ? label : ''}</H4>
                    <Nav>
                      {items?.map(({label: subItemLabel, type: subItemType, path}) => (
                        <NavItem key={path}>
                          {/* do not show cookie banner on landing page */}
                          {!isLandingPage && path === cookieSettingsPath && (
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
                            <span
                              role="button"
                              className="fake-link"
                              onClick={() => removeUserConsentAndReload()}
                            >
                              {t(subItemLabel)}
                            </span>
                          )}
                          {path !== cookieSettingsPath && (
                            <Link
                              type={subItemType !== 'link' ? 'anchor' : 'link'}
                              to={path}
                              targetBlank={subItemType === 'link'}
                            >
                              {t(subItemLabel)}
                            </Link>
                          )}
                        </NavItem>
                      ))}
                    </Nav>
                  </Col>
                )
              })}
            </Row>
          </Col>
          <Col col={12}>
            <FooterSection second t={t} extraLinks={extraLinks} />
          </Col>
        </Row>
        <Row>
          <Col>
            {footerText && (
              <Row mb={footerSubText ? 0 : 32}>
                <Col>
                  <Caption color={white}>{t(footerText)}</Caption>
                </Col>
              </Row>
            )}
            {footerSubText && (
              <Row mb={32}>
                <Col>
                  <Caption color={white}>{t(footerSubText)}</Caption>
                </Col>
              </Row>
            )}
            <Copyright>
              {t('footer.copyright', {
                year: new Date().getFullYear(),
                companyName: COMPANY.NAME_NETHERLANDS,
              })}
            </Copyright>
            {isCnTldOrRegion(region) === true && (
              <Copyright>
                <Trans
                  t={t}
                  i18nKey="footer.icp-cn"
                  components={{
                    a: (
                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                      <Link targetBlank type="anchor">
                        Link
                      </Link>
                    ),
                  }}
                />
              </Copyright>
            )}
          </Col>
        </Row>
      </Container>
    </FooterWrapper>
  )
}

Footer.propTypes = footerPropTypes

Footer.defaultProps = footerDefaultPropTypes

export default Footer
